import styles from 'css/src/components/pricing/PricingPlanInfo.module.scss';

import React from 'react';

import { BillingCycleEnum, type Plan } from 'js/src/client';
import Badge from 'js/src/components/common/ui/Badge';
import ContentPlaceholder from 'js/src/components/common/ui/ContentPlaceholder';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import UpgradeButton from 'js/src/components/pricing/UpgradeButton';

const PricingPlanInfo = React.memo(function PricingPlanInfo({
    plan,
    switchToPlan,
    saleSubHeading,
    planSubHeading,
    handlePlanSwitchError,
}: {
    plan?: Plan;
    switchToPlan: (plan: Plan) => void;
    saleSubHeading?: string;
    planSubHeading?: string;
    handlePlanSwitchError: (error_info: PlanSwitchErrorInfo) => void;
}) {
    let pricing_block = null;
    let billing_interval = null;
    let featured_label = null;
    let saleSubHeadingNode = null;
    let savingsMessage = null;
    let showSavingsMessage = false;
    let planDisplayName = null;
    let planDescription = null;

    if (!plan) {
        // Skeleton loading state
        pricing_block = (
            <div className={styles['price']}>
                <ContentPlaceholder styleProps={{ width: '80px', height: '32px' }} />
            </div>
        );
        billing_interval = <ContentPlaceholder styleProps={{ width: '120px', height: '16px' }} />;
        planDisplayName = <ContentPlaceholder styleProps={{ width: '100px', height: '20px' }} />;
        planDescription = <ContentPlaceholder styleProps={{ width: '200px', height: '16px' }} />;
        savingsMessage = <div className="savings-container"></div>;
    } else {
        // Normal rendering with plan data
        if (plan.is_enterprise) {
            pricing_block = (
                <div className={styles['price']}>
                    <span className="dollar-value">Custom</span>
                </div>
            );
            billing_interval = 'billed yearly';
        } else if (plan.name === '') {
            billing_interval = 'forever free';
            pricing_block = (
                <div className={styles['price']}>
                    <span className="dollar-sign">$</span>
                    <span className="dollar-value">0</span>
                </div>
            );
        } else {
            let display_price = plan.price;
            if (plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                display_price = display_price / 12.0; // show per month
            }
            const displayPriceStr =
                display_price % 1 !== 0 ? display_price.toFixed(2) : display_price;
            billing_interval = 'per month';
            if (plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                billing_interval = `${billing_interval}, billed yearly`;
            }
            pricing_block = (
                <div className={styles['price']}>
                    <span className="dollar-sign">$</span>
                    <span className="dollar-value">{displayPriceStr}</span>
                    <span className={styles['dollar-currency']}> USD</span>
                </div>
            );
        }

        if (plan.featured) {
            featured_label = <Badge>POPULAR CHOICE</Badge>;
        }

        if (saleSubHeading) {
            saleSubHeadingNode = (
                <div className="mb-l5-8 sale-sub-heading">
                    <b className="text-danger">{saleSubHeading}</b>
                </div>
            );
        }

        const savingsPlaceholder = (
            <div
                className={`savings-container ${plan.display_name === 'Enterprise' ? 'enterprise-block' : ''}`}
            ></div>
        );
        savingsMessage = savingsPlaceholder;
        switch (plan.display_name) {
            case 'Starter':
                savingsMessage = <div className={styles['savings']}>Save 25%</div>;
                break;
            case 'Professional':
                savingsMessage = <div className={styles['savings']}>Save 25%</div>;
                break;
        }

        showSavingsMessage =
            !/special-pricing/.test(window.location.pathname) &&
            plan.billing_cycle === BillingCycleEnum.ANNUAL;

        planDisplayName = plan.display_name;
        planDescription = plan.description;
    }

    return (
        <div className={styles['pricing-plan-info']}>
            <div className={`row ${styles['feature-label-row']}`}>
                <div className="col-md-12">{featured_label}</div>
            </div>
            <div className="row plan-title-row">
                <div className="col-md-12">
                    <h6>{planDisplayName}</h6>
                </div>
            </div>
            <div className={`row ${styles['price-container-row']}`}>
                <div className="col-md-12">
                    <div className={styles['price-container']}>
                        {pricing_block}
                        {saleSubHeadingNode}
                    </div>
                </div>
            </div>
            <div className="row billing-interval-row pt-l5-16">
                <div className="col-md-12">
                    <div className={styles['price-container']}>
                        {billing_interval}
                        {planSubHeading || ''}
                        {showSavingsMessage ? (
                            savingsMessage
                        ) : plan ? (
                            <div className="savings-container"></div>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="row button-row">
                <div className="col-md-12">
                    {plan ? (
                        <UpgradeButton
                            plan={plan}
                            switchToPlan={switchToPlan}
                            handlePlanSwitchError={handlePlanSwitchError}
                            className="w-100"
                        />
                    ) : (
                        <ContentPlaceholder styleProps={{ width: '100%', height: '40px' }} />
                    )}
                </div>
            </div>
            <div className="row my-l5-24">
                <div className="col-md-12">
                    <p className="plan-description m-l5-0">{planDescription}</p>
                </div>
            </div>
        </div>
    );
});

export default PricingPlanInfo;
